import { useState, useEffect } from 'react';
import { useAuth, authFetch } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageSEO } from '../components/PageSEO';
import { ProOnlyGate } from '../components/ProOnlyGate';
import { ToolFeedback } from '../components/ToolFeedback';
import { DocumentFieldHelp } from '../components/retirement/DocumentFieldHelp';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Sparkles, FileText, Landmark, ClipboardList, Activity,
  ExternalLink, AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight,
  CalendarIcon, Loader2, Info, Check, ShieldAlert, TrendingUp, Wallet,
  Target, Users, ArrowRight, PiggyBank, MessageCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IMSS_CONSTANCIA_URL = 'https://serviciosdigitales.imss.gob.mx/semanascotizadas-web/usuarios/IngresoAsegurado';

// TODO: placeholder — Fernando todavía no tiene el número definitivo de asesoría, reemplazar antes de publicar.
const WHATSAPP_ASESORIA_NUMBER = '5215500000000';
const WHATSAPP_ASESORIA_URL = `https://wa.me/${WHATSAPP_ASESORIA_NUMBER}?text=${encodeURIComponent('Hola, quiero información sobre asesoría para mi pensión IMSS.')}`;

const STEPS = [
  { num: 1, icon: Sparkles, label: 'Bienvenida' },
  { num: 2, icon: FileText, label: 'Guía IMSS' },
  { num: 3, icon: Landmark, label: 'Guía AFORE' },
  { num: 4, icon: ClipboardList, label: 'Tus datos' },
  { num: 5, icon: Activity, label: 'Diagnóstico' },
  { num: 6, icon: TrendingUp, label: 'Tu pensión' },
  { num: 7, icon: Target, label: 'Ahorro y meta' },
];

const RAZON_ASESORIA_LABELS = {
  brecha_semanas_no_cerrable: 'Las semanas que te faltan no alcanzan a cerrarse de forma realista antes de tu edad límite para cotizar.',
  historial_mixto_imss_issste: 'Cotizaste tanto en IMSS como en ISSSTE, y eso necesita un cálculo especial.',
  lagunas_sin_explicacion: 'Nos contaste que tienes periodos sin cotizar que no puedes explicar.',
  semanas_descontadas_significativas: 'Tu Constancia muestra semanas descontadas que vale la pena revisar.',
  cerca_del_retiro_bajo_el_minimo: 'Te quedan 3 años o menos para tu retiro y todavía no llegas al mínimo de semanas.',
};

const PLAZO_LABELS = {
  '6_meses': '6 meses',
  '1_anio': '1 año',
  '2_anios': '2 años',
  '3_anios': '3 años',
  '5_anios': '5 años',
};

const fmtMXN = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(v || 0);

function asesoriaSignalsKey(userId) {
  return `klarvi_retiro_asesoria_signals_${userId}`;
}

// Ancla explícita en vez de dejar que el navegador resuelva href="#id": si el elemento ya está
// montado, hacemos el scroll nosotros mismos. behavior:'instant' a propósito, no 'smooth' — se
// probó en vivo que scrollIntoView({behavior:'smooth'}) no hace NADA en navegadores con animaciones
// reducidas (prefers-reduced-motion / config de accesibilidad de Windows), lo que recrea el bug
// original ("el enlace no hace nada"). instant es menos vistoso pero funciona siempre.
// Si el elemento no existe todavía, no bloqueamos el comportamiento nativo del <a href>.
function scrollToSection(e, id) {
  const el = document.getElementById(id);
  if (el) {
    e.preventDefault();
    el.scrollIntoView({ behavior: 'instant', block: 'start' });
  }
}

function montoDe(obj) {
  if (!obj) return null;
  return 'monto_mensual_final' in obj ? obj.monto_mensual_final : obj.renta_mensual_estimada;
}

function buildMetaOptions(piso, techo) {
  if (!piso || !techo || techo < piso) return [];
  const step = 5000;
  const options = [];
  const start = Math.ceil(piso / step) * step;
  for (let v = start; v <= techo; v += step) options.push(v);
  const pisoRedondeado = Math.round(piso);
  const techoRedondeado = Math.round(techo);
  if (!options.length || options[0] !== pisoRedondeado) options.unshift(pisoRedondeado);
  if (options[options.length - 1] !== techoRedondeado) options.push(techoRedondeado);
  return [...new Set(options)];
}

function toISODateLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseISODateLocal(value) {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function DateField({ label, value, onChange, testId, fromYear = 1940 }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid={testId}>
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, 'dd/MM/yyyy', { locale: es }) : <span className="text-muted-foreground">Seleccionar</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            disabled={(date) => date > new Date()}
            captionLayout="dropdown-buttons"
            fromYear={fromYear}
            toYear={new Date().getFullYear()}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function NumField({ label, value, onChange, placeholder, help, testId, step: inputStep = '1', min = '0' }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input
        type="number"
        min={min}
        step={inputStep}
        placeholder={placeholder || '0'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}

function PensionCell({ label, cell, ley97, notaAforeSoloRendimiento }) {
  if (!cell) return null;
  const vp = cell.valor_presente;
  if (vp && vp.elegible === false) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4 space-y-2">
          <p className="text-sm font-semibold text-amber-900">{label}</p>
          <p className="text-sm text-amber-800">{vp.motivo}</p>
          {cell.afore_lump_sum != null && (
            <p className="text-xs text-amber-800 flex items-center gap-1">
              <Wallet className="w-3.5 h-3.5" /> De todas formas, tu saldo AFORE completo hoy ({fmtMXN(cell.afore_lump_sum)}
              {ley97 ? ': Retiro + Vivienda + Voluntario' : ''}) es tuyo: aunque todavía no califiques a una
              pensión mensual, lo puedes retirar en una sola exhibición.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }
  const montoVP = montoDe(vp);
  const montoVF = montoDe(cell.valor_futuro);
  return (
    <Card className="border">
      <CardContent className="p-5 space-y-3">
        <p className="text-sm font-semibold text-[#0F172A] font-['Manrope']">{label}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p
              className="text-xs text-muted-foreground underline decoration-dotted cursor-help"
              title="Lo que este monto vale hoy, en tu poder de compra actual (ya con la inflación descontada)."
            >
              Valor presente
            </p>
            <p className="text-lg font-bold text-[#0F172A] font-['Manrope']">
              {fmtMXN(montoVP)}<span className="text-xs font-normal text-muted-foreground">/mes</span>
            </p>
          </div>
          <div>
            <p
              className="text-xs text-muted-foreground underline decoration-dotted cursor-help"
              title="El mismo monto, pero en los pesos que tendrás en esa fecha, con la inflación ya incluida (no significa más poder de compra real)."
            >
              Valor futuro
            </p>
            <p className="text-lg font-bold text-[#0F172A] font-['Manrope']">
              {fmtMXN(montoVF)}<span className="text-xs font-normal text-muted-foreground">/mes</span>
            </p>
          </div>
        </div>
        {vp?.aplica_pension_minima_garantizada && (
          <p className="text-xs text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Aplica tu Pensión Mínima Garantizada.
          </p>
        )}
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Wallet className="w-3.5 h-3.5" />
          {ley97
            ? ` Pago en una sola exhibición: ${fmtMXN(cell.afore_lump_sum)} (esto es solo tu subcuenta de Vivienda — tu Retiro y Voluntario ya están en la renta mensual de arriba).`
            : ` Pago en una sola exhibición (tu AFORE): ${fmtMXN(cell.afore_lump_sum)}`}
        </p>
        {!ley97 && notaAforeSoloRendimiento && (
          <p className="text-xs text-muted-foreground flex items-start gap-1">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            Este monto solo suma el rendimiento de tu saldo actual — no incluye las aportaciones nuevas
            que harías en estos años.
          </p>
        )}
        {ley97 && (
          <a
            href="#explicacion-afore"
            onClick={(e) => scrollToSection(e, 'explicacion-afore')}
            className="text-xs text-[#2563EB] hover:underline font-medium inline-block"
          >
            ¿Qué significa este monto? →
          </a>
        )}
      </CardContent>
    </Card>
  );
}

function ProximoPasoBanner({ titulo, cuerpo, ctaLabel, ctaHref }) {
  return (
    <Card className="border-2 border-[#2563EB] bg-blue-50/40" data-testid="proximo-paso-banner">
      <CardContent className="p-6 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#2563EB]">Tu próximo paso</p>
        <h3 className="text-lg font-semibold text-[#0F172A] font-['Manrope']">{titulo}</h3>
        <p className="text-sm text-[#0F172A]">{cuerpo}</p>
        {ctaLabel && ctaHref && (
          <a
            href={ctaHref}
            onClick={(e) => scrollToSection(e, ctaHref.slice(1))}
            className="inline-flex items-center gap-1 text-sm font-medium text-[#2563EB] hover:underline"
          >
            {ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
          </a>
        )}
      </CardContent>
    </Card>
  );
}

function ExplicacionItem({ titulo, children }) {
  return (
    <details className="group border-t first:border-t-0 pt-3 first:pt-0">
      <summary className="flex items-center justify-between gap-2 cursor-pointer list-none text-sm font-medium text-[#0F172A]">
        {titulo}
        <ChevronRight className="w-4 h-4 flex-shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
      </summary>
      <div className="mt-2 text-sm text-muted-foreground space-y-2">
        {children}
      </div>
    </details>
  );
}

function ExplicacionAforeAccordion({ rendimientoAforePct }) {
  return (
    <Card className="border scroll-mt-20" id="explicacion-afore" data-testid="explicacion-afore">
      <CardContent className="p-4 space-y-3">
        <p className="text-sm font-semibold text-[#0F172A] font-['Manrope']">Explícamelo más a fondo</p>
        <div className="space-y-3">
          <ExplicacionItem titulo="¿Cómo crece tu AFORE aunque ya no cotices?">
            <p>
              Tu saldo AFORE no se queda quieto: mientras siga invertido en tu SIEFORE, sigue generando
              rendimiento cada año — cotices o no. Es como una alcancía que, además de guardar lo que
              metes, le suma intereses solita. El sistema de AFOREs ha rendido en promedio {rendimientoAforePct.toFixed(1)}% nominal
              anual en años recientes (varía según tu SIEFORE — ver la fuente al final de esta pantalla).
            </p>
            <p>
              Un aviso honesto: ahora mismo, la columna "Valor futuro" de esta pantalla no incluye ese
              rendimiento — solo ajusta el monto de hoy al crecimiento general de precios (UMA), para
              comparar escenarios entre sí. Si quieres ver el efecto real de que tu saldo siga rindiendo
              con el tiempo, revisa la sección de Ahorro voluntario más abajo, que sí lo calcula.
            </p>
          </ExplicacionItem>
          <ExplicacionItem titulo="Renta vitalicia vs. retiro programado: dos caminos distintos">
            <p>Cuando llegue tu retiro, tu saldo AFORE se puede convertir en pensión de dos formas:</p>
            <p>
              <strong>Renta vitalicia:</strong> transfieres tu saldo a una aseguradora autorizada por el
              IMSS, que se compromete a pagarte una mensualidad fija de por vida — sin importar cuántos
              años vivas. El riesgo de "vivir más de lo calculado" lo absorbe la aseguradora, no tú.
              Normalmente también contratas un seguro de sobrevivencia para tus beneficiarios.
            </p>
            <p>
              <strong>Retiro programado:</strong> tu dinero se queda en tu AFORE, y cada año se recalcula
              cuánto puedes retirar según tu saldo restante y tu esperanza de vida. Sigue generando
              rendimiento mientras tanto, pero si vives más años de los calculados, el saldo se puede
              agotar.
            </p>
            <p>
              Klarvi estima tu renta dividiendo tu saldo entre tu esperanza de vida — una aproximación
              simple, más parecida en espíritu a una renta fija que a un retiro programado que se ajusta
              año con año. No es el cálculo actuarial real de ninguna de las dos: la elección de verdad la
              haces al momento de pensionarte.
            </p>
          </ExplicacionItem>
          <ExplicacionItem titulo="¿Qué pasa si vivo más años de los que se calcularon?">
            <p>Depende de qué camino elijas:</p>
            <p>
              Con <strong>renta vitalicia</strong>, no pasa nada: la aseguradora te sigue pagando lo mismo
              cada mes, vivas los años que vivas. Para eso existe — para que ese riesgo no sea tuyo.
            </p>
            <p>
              Con <strong>retiro programado</strong>, si tu saldo se agota, no te quedas sin nada: existe
              una Pensión Garantizada del gobierno como piso, siempre que cumplas los requisitos de tu ley
              (semanas cotizadas y edad). No tenemos verificado el monto exacto para publicarlo aquí con
              confianza — te recomendamos confirmarlo directamente con el IMSS o con tu aseguradora cuando
              te acerques a tu retiro.
            </p>
          </ExplicacionItem>
        </div>
      </CardContent>
    </Card>
  );
}

function AforeLey73Info({ rendimientoAforePct }) {
  return (
    <Card className="border" data-testid="afore-ley73-info">
      <CardContent className="p-4 space-y-3">
        <p className="text-sm font-semibold text-[#0F172A] font-['Manrope']">Tu AFORE, aparte de tu pensión</p>
        <div className="text-sm text-muted-foreground space-y-2">
          <p>
            En Ley 73, tu pensión mensual la calcula el IMSS con tu salario y tus semanas cotizadas — no
            depende de tu saldo AFORE. Pero ese saldo también es tuyo, aparte: nunca del gobierno ni del
            IMSS, y se te entrega en una sola exhibición al momento de tu retiro.
          </p>
          <p>
            Mientras siga invertido en tu SIEFORE, ese saldo sigue generando rendimiento cada año — el
            sistema de AFOREs ha rendido en promedio {rendimientoAforePct.toFixed(1)}% nominal anual en años recientes (varía según tu
            SIEFORE). Por eso el "pago en una sola exhibición" que ves en Escenario 4 es mayor que el de
            Escenario 1: entre más tiempo pase antes de tu retiro, más tiempo tiene ese saldo para crecer.
            (Nota: hoy ese número no incluye lo que aportarías de más si sigues cotizando esos años —
            solo el crecimiento de lo que ya tienes ahorrado.)
          </p>
          <p>
            Además de lo obligatorio, puedes aportar voluntariamente a tu AFORE en cualquier momento —
            revisa la sección de Ahorro voluntario más abajo para ver el efecto de eso en tu saldo.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

const RETIREMENT_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Planeación de Retiro - Klarvi",
  "url": "https://klarvi.org/retiro",
  "description": "Planea tu retiro en México: AFORE, semanas cotizadas y proyección de pensión bajo Ley 73 o Ley 97.",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "inLanguage": "es-MX",
};

export default function RetirementPage() {
  const { user } = useAuth();
  const isPro = user?.plan === 'pro';

  const [step, setStep] = useState(1);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);

  const [scenarios, setScenarios] = useState(null);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [plazoEscenario2, setPlazoEscenario2] = useState('1_anio');
  const [plazoEscenario3, setPlazoEscenario3] = useState('1_anio');

  const [historialIssste, setHistorialIssste] = useState(false);
  const [descuentosSignificativos, setDescuentosSignificativos] = useState(false);
  const [asesoriaCheck, setAsesoriaCheck] = useState(null);
  const [asesoriaCtaClicked, setAsesoriaCtaClicked] = useState(false);

  const [aporteVoluntario, setAporteVoluntario] = useState('');
  const [mesesVoluntario, setMesesVoluntario] = useState('120');
  const [ahorroResult, setAhorroResult] = useState(null);
  const [loadingAhorro, setLoadingAhorro] = useState(false);

  const [metaRango, setMetaRango] = useState(null);
  const [loadingMetaRango, setLoadingMetaRango] = useState(false);
  const [metaSeleccionada, setMetaSeleccionada] = useState('');
  const [metaNivel2Monto, setMetaNivel2Monto] = useState('');
  const [metaResult, setMetaResult] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const [fechaNacimiento, setFechaNacimiento] = useState(null);
  const [fechaPrimeraCotizacion, setFechaPrimeraCotizacion] = useState(null);
  const [formData, setFormData] = useState({
    nss: '',
    semanas_cotizadas: '',
    sbc_mas_reciente: '',
    saldo_afore: { retiro_rcv: '', vivienda: '', voluntario: '' },
    tiene_lagunas: false,
    lagunas_descripcion: '',
  });

  const [rendimientoAforePct, setRendimientoAforePct] = useState(7.0);

  useEffect(() => {
    const fetchFiscalInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/fiscal/info`);
        if (res.ok) {
          const data = await res.json();
          setRendimientoAforePct(data.rendimiento_nominal_anual_estimado_pct);
        }
      } catch {}
    };
    fetchFiscalInfo();
  }, []);

  useEffect(() => {
    if (!isPro) {
      setLoadingProfile(false);
      authFetch(`${API_URL}/api/retirement/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: 'upsell_view' }),
      }).catch(() => { /* non-blocking */ });
      return;
    }
    (async () => {
      try {
        const response = await authFetch(`${API_URL}/api/retirement/profile`);
        if (response.ok) {
          const data = await response.json();
          if (data.profile) {
            const p = data.profile;
            setFechaNacimiento(parseISODateLocal(p.fecha_nacimiento));
            setFechaPrimeraCotizacion(parseISODateLocal(p.fecha_primera_cotizacion));
            setFormData({
              nss: p.nss || '',
              semanas_cotizadas: String(p.semanas_cotizadas ?? ''),
              sbc_mas_reciente: String(p.sbc_mas_reciente ?? ''),
              saldo_afore: {
                retiro_rcv: String(p.saldo_afore?.retiro_rcv ?? ''),
                vivienda: String(p.saldo_afore?.vivienda ?? ''),
                voluntario: String(p.saldo_afore?.voluntario ?? ''),
              },
              tiene_lagunas: !!p.tiene_lagunas,
              lagunas_descripcion: p.lagunas_descripcion || '',
            });
            setDiagnosis(data.diagnosis);
            setStep(5);
          }
        }
      } catch {
        /* non-blocking: usuario simplemente parte del formulario vacío */
      } finally {
        setLoadingProfile(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro]);

  useEffect(() => {
    const handle = setTimeout(() => {
      if (step === 7) loadAhorroVoluntario(aporteVoluntario, mesesVoluntario);
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aporteVoluntario, mesesVoluntario, step]);

  useEffect(() => {
    if (!user?.user_id) return;
    try {
      const saved = localStorage.getItem(asesoriaSignalsKey(user.user_id));
      if (saved) {
        const parsed = JSON.parse(saved);
        setHistorialIssste(!!parsed.historialIssste);
        setDescuentosSignificativos(!!parsed.descuentosSignificativos);
      }
    } catch {
      /* localStorage no disponible o corrupto: se queda en false por defecto */
    }
  }, [user?.user_id]);

  if (!isPro) {
    return (
      <Layout>
        <PageSEO
          title="Planeación de Retiro: AFORE y Pensión México | Klarvi"
          description="Planea tu retiro en México: revisa tu AFORE, semanas cotizadas y proyecta tu pensión bajo Ley 73 o Ley 97. Descubre si vas por buen camino."
          canonical="https://klarvi.org/retiro"
          jsonLd={RETIREMENT_JSON_LD}
        />
        <ProOnlyGate
          valueContext="Aquí puedes obtener información sobre tu situación de retiro basada en tus datos: tus semanas cotizadas, qué ley te aplica, y qué alternativas tienes para mejorar tu pensión."
        />
      </Layout>
    );
  }

  const updateAforeField = (field, value) => {
    setFormData((prev) => ({ ...prev, saldo_afore: { ...prev.saldo_afore, [field]: value } }));
  };

  const canSubmitForm = () => {
    if (!formData.nss.trim() || !fechaNacimiento || !fechaPrimeraCotizacion) return false;
    if (formData.semanas_cotizadas === '' || Number(formData.semanas_cotizadas) < 0) return false;
    if (!formData.sbc_mas_reciente || Number(formData.sbc_mas_reciente) <= 0) return false;
    if (formData.tiene_lagunas && !formData.lagunas_descripcion.trim()) return false;
    return true;
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!canSubmitForm()) return;

    setSaving(true);
    try {
      const payload = {
        nss: formData.nss.trim(),
        fecha_nacimiento: toISODateLocal(fechaNacimiento),
        fecha_primera_cotizacion: toISODateLocal(fechaPrimeraCotizacion),
        semanas_cotizadas: parseInt(formData.semanas_cotizadas, 10) || 0,
        sbc_mas_reciente: parseFloat(formData.sbc_mas_reciente) || 0,
        saldo_afore: {
          retiro_rcv: parseFloat(formData.saldo_afore.retiro_rcv) || 0,
          vivienda: parseFloat(formData.saldo_afore.vivienda) || 0,
          voluntario: parseFloat(formData.saldo_afore.voluntario) || 0,
        },
        tiene_lagunas: formData.tiene_lagunas,
        lagunas_descripcion: formData.tiene_lagunas ? formData.lagunas_descripcion.trim() : null,
      };

      const response = await authFetch(`${API_URL}/api/retirement/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || 'Error al guardar tus datos');
        return;
      }

      const data = await response.json();
      setDiagnosis(data.diagnosis);
      setScenarios(null);
      setStep(5);
      toast.success('Diagnóstico generado');
    } catch {
      toast.error('Error al guardar tus datos');
    } finally {
      setSaving(false);
    }
  };

  const checkAsesoria = async (issste, descuentos) => {
    try {
      const params = new URLSearchParams({
        historial_issste: String(issste),
        descuentos_significativos: String(descuentos),
      });
      const response = await authFetch(`${API_URL}/api/retirement/asesoria-check?${params}`);
      if (response.ok) setAsesoriaCheck(await response.json());
    } catch {
      /* non-blocking */
    }
  };

  const loadScenarios = async () => {
    setStep(6);
    if (scenarios) return;
    setLoadingScenarios(true);
    try {
      const response = await authFetch(`${API_URL}/api/retirement/pension-scenarios`);
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || 'Error al calcular tu pensión estimada');
        return;
      }
      const data = await response.json();
      setScenarios(data);
      if (data.estado_elegibilidad === 'cercano' && data.escenario_2?.plazo_sugerido) {
        setPlazoEscenario2(data.escenario_2.plazo_sugerido);
        setPlazoEscenario3(data.escenario_2.plazo_sugerido);
      }
      await checkAsesoria(historialIssste, descuentosSignificativos);
    } catch {
      toast.error('Error al calcular tu pensión estimada');
    } finally {
      setLoadingScenarios(false);
    }
  };

  const persistAsesoriaSignals = (issste, descuentos) => {
    if (!user?.user_id) return;
    try {
      localStorage.setItem(
        asesoriaSignalsKey(user.user_id),
        JSON.stringify({ historialIssste: issste, descuentosSignificativos: descuentos })
      );
    } catch {
      /* no-op: si localStorage falla, la señal simplemente no persiste entre visitas */
    }
  };

  const handleIsssteChange = (checked) => {
    setHistorialIssste(checked);
    persistAsesoriaSignals(checked, descuentosSignificativos);
    checkAsesoria(checked, descuentosSignificativos);
  };

  const handleDescuentosChange = (checked) => {
    setDescuentosSignificativos(checked);
    persistAsesoriaSignals(historialIssste, checked);
    checkAsesoria(historialIssste, checked);
  };

  const handleAsesoriaCtaClick = async () => {
    setAsesoriaCtaClicked(true);
    try {
      // Nombre de evento sin renombrar a propósito: ya hay historial de analítica guardado con
      // 'gestoria_cta_click' (ver RETIREMENT_TRACKABLE_EVENTS en server.py); cambiarlo cortaría esa serie.
      await authFetch(`${API_URL}/api/retirement/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: 'gestoria_cta_click' }),
      });
      toast.success('Registramos tu interés en asesoría. Te contactaremos pronto.');
    } catch {
      toast.error('No pudimos registrar tu interés, intenta de nuevo.');
      setAsesoriaCtaClicked(false);
    }
  };

  const loadAhorroVoluntario = async (aporte, meses) => {
    if (!aporte || Number(aporte) <= 0 || !meses || Number(meses) <= 0) {
      setAhorroResult(null);
      return;
    }
    setLoadingAhorro(true);
    try {
      const response = await authFetch(`${API_URL}/api/retirement/ahorro-voluntario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aporte_mensual: Number(aporte), meses: Number(meses) }),
      });
      if (response.ok) setAhorroResult(await response.json());
    } catch {
      /* non-blocking */
    } finally {
      setLoadingAhorro(false);
    }
  };

  const loadMetaRango = async () => {
    if (metaRango) return;
    setLoadingMetaRango(true);
    try {
      const response = await authFetch(`${API_URL}/api/retirement/meta-inversa-rango`);
      if (response.ok) setMetaRango(await response.json());
    } catch {
      /* non-blocking */
    } finally {
      setLoadingMetaRango(false);
    }
  };

  const submitMetaInversa = async (metaMensual, conAsesoria) => {
    setLoadingMeta(true);
    try {
      const response = await authFetch(`${API_URL}/api/retirement/meta-inversa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meta_mensual: metaMensual, con_asesoria: conAsesoria }),
      });
      if (!response.ok) {
        toast.error('Error al calcular tu ruta');
        return;
      }
      const data = await response.json();
      setMetaResult(data);
      if (data.estado === 'requiere_confirmacion_asesoria') {
        setMetaNivel2Monto(String(metaMensual));
      }
    } catch {
      toast.error('Error al calcular tu ruta');
    } finally {
      setLoadingMeta(false);
    }
  };

  const renderWelcome = () => (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Klarvi te ayuda a entender en qué punto vas de tu retiro. Este primer diagnóstico toma unos minutos y se hace en 3 pasos:
      </p>
      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { n: 1, title: 'Consigue tus documentos', desc: 'Te guiamos para descargar tu Constancia de Semanas Cotizadas del IMSS y tu estado de cuenta AFORE.' },
          { n: 2, title: 'Captura tus datos', desc: 'Escribe los datos clave de esos documentos en un formulario simple. Te decimos exactamente dónde encontrarlos.' },
          { n: 3, title: 'Obtén tu diagnóstico', desc: 'Ve tus semanas cotizadas, las que te faltan, y qué ley de pensión te aplica.' },
        ].map((s) => (
          <Card key={s.n} className="border">
            <CardContent className="p-5">
              <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-sm font-semibold mb-3">
                {s.n}
              </div>
              <h3 className="font-semibold text-[#0F172A] font-['Manrope']">{s.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <Button onClick={() => setStep(2)} data-testid="welcome-start-btn">
        Comenzar <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );

  const renderGuiaImss = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-[#0F172A] font-['Manrope'] mb-2">Cómo sacar tu Constancia de Semanas Cotizadas</h3>
        <a href={IMSS_CONSTANCIA_URL} target="_blank" rel="noopener noreferrer" data-testid="imss-portal-link">
          <Button variant="outline" className="w-full sm:w-auto">
            Ir al portal del IMSS <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </a>
        <ol className="mt-4 space-y-2 text-sm text-muted-foreground list-decimal list-inside">
          <li>Entra al portal con el botón de arriba.</li>
          <li>Captura tu <strong>CURP</strong> y tu <strong>NSS</strong>.</li>
          <li>Ingresa tu <strong>correo electrónico</strong> y <strong>confírmalo</strong>.</li>
          <li>Ingresa tu <strong>teléfono</strong> y <strong>confírmalo</strong>.</li>
          <li>Resuelve el <strong>captcha</strong>.</li>
          <li>Descarga tu constancia en PDF.</li>
        </ol>
      </div>
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 text-amber-800 font-semibold">
            <AlertTriangle className="w-4 h-4" />
            Errores comunes
          </div>
          <p className="mt-2 text-sm text-amber-900">
            <strong>Access Denied — Error 17:</strong> es un bloqueo del portal del IMSS, no de tu cuenta. Prueba lo siguiente:
          </p>
          <ul className="mt-2 space-y-1 text-sm text-amber-900 list-disc list-inside">
            <li>Usa una ventana de <strong>modo incógnito</strong> del navegador.</li>
            <li>Desactiva tu <strong>VPN</strong> si tienes una activa.</li>
            <li>Intenta más tarde — el portal suele saturarse en ciertos horarios.</li>
            <li>Como alternativa, usa la app <strong>IMSS Digital</strong> desde tu celular.</li>
          </ul>
        </CardContent>
      </Card>
      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={() => setStep(1)} data-testid="guia-imss-prev-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
        </Button>
        <Button onClick={() => setStep(3)} data-testid="guia-imss-next-btn">
          Siguiente <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  const renderGuiaAfore = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-[#0F172A] font-['Manrope'] mb-2">Cómo sacar tu estado de cuenta AFORE</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Existen varias AFOREs distintas, así que los pasos exactos varían un poco según la tuya. En general:
        </p>
        <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
          <li>Identifica cuál es tu AFORE. Si no la recuerdas, puedes consultarla en <strong>e5cinco.gob.mx</strong> (el localizador de CONSAR) con tu CURP.</li>
          <li>Descarga la app o entra al portal web de esa AFORE.</li>
          <li>Inicia sesión con tu usuario, o regístrate con tu CURP y NSS si es tu primera vez.</li>
          <li>Busca la sección <strong>"Estado de cuenta"</strong> o <strong>"Mi ahorro"</strong>.</li>
          <li>Descarga el PDF más reciente.</li>
        </ol>
      </div>
      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={() => setStep(2)} data-testid="guia-afore-prev-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
        </Button>
        <Button onClick={() => setStep(4)} data-testid="guia-afore-next-btn">
          Siguiente <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  const renderFormulario = () => (
    <form onSubmit={handleSubmitForm} className="space-y-6">
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-900">
            Los resultados de este diagnóstico dependen completamente de la exactitud de los datos que captures.
            Klarvi no tiene acceso a las bases de datos del IMSS ni de CONSAR, por lo que no valida esta información.
          </p>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="nss">NSS (Número de Seguridad Social)</Label>
            <DocumentFieldHelp docType="semanas" highlight="nss" title="¿Dónde encuentro mi NSS?" description="Aparece en la parte superior de tu Constancia de Semanas Cotizadas." />
          </div>
          <Input
            id="nss"
            value={formData.nss}
            onChange={(e) => setFormData((p) => ({ ...p, nss: e.target.value }))}
            placeholder="11 dígitos"
            maxLength={11}
            data-testid="nss-input"
          />
        </div>

        <DateField label="Fecha de nacimiento (referencia)" value={fechaNacimiento} onChange={setFechaNacimiento} testId="fecha-nacimiento-btn" />

        <div className="sm:col-span-2 space-y-1">
          <div className="flex items-center justify-between">
            <Label>Fecha de primera cotización al IMSS</Label>
            <DocumentFieldHelp docType="semanas" highlight="fecha_primera" title="¿Dónde encuentro mi fecha de primera cotización?" description="Es la fecha de tu primer registro ante el IMSS, dentro de tu Constancia de Semanas Cotizadas. Determina si te aplica Ley 73 o Ley 97." />
          </div>
          <DateField value={fechaPrimeraCotizacion} onChange={setFechaPrimeraCotizacion} testId="fecha-primera-cotizacion-btn" fromYear={1960} />
          <p className="text-xs text-muted-foreground">Antes del 1 de julio de 1997: Ley 73. A partir de esa fecha: Ley 97.</p>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="semanas">Semanas cotizadas</Label>
            <DocumentFieldHelp docType="semanas" highlight="semanas" title="¿Dónde encuentro mis semanas cotizadas?" description="Es el total que aparece en tu Constancia de Semanas Cotizadas." />
          </div>
          <Input
            id="semanas"
            type="number"
            min="0"
            value={formData.semanas_cotizadas}
            onChange={(e) => setFormData((p) => ({ ...p, semanas_cotizadas: e.target.value }))}
            placeholder="812"
            data-testid="semanas-cotizadas-input"
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="sbc">SBC más reciente (Salario Base de Cotización)</Label>
            <DocumentFieldHelp docType="semanas" highlight="sbc" title="¿Dónde encuentro mi SBC?" description="El SBC es distinto a tu salario bruto: incluye solo los conceptos que la ley considera para cotizar al IMSS. Suele aparecer en tu Constancia de Semanas Cotizadas o en tu recibo de nómina." />
          </div>
          <Input
            id="sbc"
            type="number"
            min="0"
            step="0.01"
            value={formData.sbc_mas_reciente}
            onChange={(e) => setFormData((p) => ({ ...p, sbc_mas_reciente: e.target.value }))}
            placeholder="487.12"
            data-testid="sbc-input"
          />
          <p className="text-xs text-muted-foreground">El SBC no es tu sueldo bruto: es la base con la que el IMSS calcula tus cuotas y prestaciones.</p>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Saldo AFORE desglosado</Label>
          <DocumentFieldHelp docType="afore" highlight="saldo_afore" title="¿Dónde encuentro mi saldo por subcuenta?" description="En tu estado de cuenta AFORE, busca la tabla de saldo por subcuenta." />
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <NumField label="Retiro (RCV)" value={formData.saldo_afore.retiro_rcv} onChange={(v) => updateAforeField('retiro_rcv', v)} step="0.01" testId="afore-retiro-input" />
          <NumField label="Vivienda" value={formData.saldo_afore.vivienda} onChange={(v) => updateAforeField('vivienda', v)} step="0.01" testId="afore-vivienda-input" />
          <NumField label="Voluntario / complementario" value={formData.saldo_afore.voluntario} onChange={(v) => updateAforeField('voluntario', v)} step="0.01" testId="afore-voluntario-input" />
        </div>
      </div>

      <div className="space-y-2">
        <Label>¿Tienes lagunas o periodos sin cotizar que no puedas explicar?</Label>
        <RadioGroup
          value={formData.tiene_lagunas ? 'si' : 'no'}
          onValueChange={(v) => setFormData((p) => ({ ...p, tiene_lagunas: v === 'si' }))}
          className="flex gap-6"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="no" id="lagunas-no" data-testid="lagunas-no-radio" />
            <Label htmlFor="lagunas-no" className="font-normal">No</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="si" id="lagunas-si" data-testid="lagunas-si-radio" />
            <Label htmlFor="lagunas-si" className="font-normal">Sí</Label>
          </div>
        </RadioGroup>
        {formData.tiene_lagunas && (
          <Textarea
            value={formData.lagunas_descripcion}
            onChange={(e) => setFormData((p) => ({ ...p, lagunas_descripcion: e.target.value }))}
            placeholder="Descríbelo brevemente (ej. periodo sin empleo formal, cambio de patrón sin registro, etc.)"
            data-testid="lagunas-descripcion-input"
          />
        )}
      </div>

      <div className="flex justify-between pt-2">
        <Button type="button" variant="outline" onClick={() => setStep(3)} data-testid="formulario-prev-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
        </Button>
        <Button type="submit" disabled={!canSubmitForm() || saving} data-testid="formulario-submit-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
          Ver mi diagnóstico <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </form>
  );

  const renderDiagnostico = () => {
    if (!diagnosis) return null;
    return (
      <div className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <Card className="border">
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground">Ley aplicable</p>
              <p className="mt-1 text-2xl font-bold text-[#0F172A] font-['Manrope']" data-testid="diagnosis-ley-aplicable">{diagnosis.ley_aplicable}</p>
            </CardContent>
          </Card>
          <Card className="border">
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground">Semanas cotizadas</p>
              <p className="mt-1 text-2xl font-bold text-[#0F172A] font-['Manrope']" data-testid="diagnosis-semanas-cotizadas">{formData.semanas_cotizadas}</p>
            </CardContent>
          </Card>
          <Card className="border">
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground">Semanas mínimas requeridas</p>
              <p className="mt-1 text-2xl font-bold text-[#0F172A] font-['Manrope']" data-testid="diagnosis-semanas-minimas">{diagnosis.semanas_minimas_requeridas}</p>
            </CardContent>
          </Card>
          <Card className="border">
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground">Semanas faltantes</p>
              <p className={`mt-1 text-2xl font-bold font-['Manrope'] ${diagnosis.semanas_faltantes > 0 ? 'text-amber-600' : 'text-emerald-600'}`} data-testid="diagnosis-semanas-faltantes">
                {diagnosis.semanas_faltantes}
              </p>
            </CardContent>
          </Card>
        </div>

        {diagnosis.semanas_faltantes === 0 && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-emerald-900">Ya llegaste al mínimo de semanas que necesitas — con lo que nos compartiste, esta parte ya la tienes resuelta.</p>
            </CardContent>
          </Card>
        )}

        {diagnosis.cerca_del_minimo && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-900">Estás muy cerca: te faltan {diagnosis.semanas_faltantes} semanas, poco menos de 2 años cotizando.</p>
            </CardContent>
          </Card>
        )}

        {diagnosis.alerta_lagunas && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-start gap-3">
              <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-900">Nos contaste que tienes periodos sin cotizar que no puedes explicar. Te conviene aclararlos directamente con el IMSS — podría haber semanas tuyas que todavía no se están contando a tu favor.</p>
            </CardContent>
          </Card>
        )}

        <Card className="border">
          <CardContent className="p-5 text-sm text-muted-foreground">
            Este diagnóstico es informativo y se basa en lo que tú nos compartiste.
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row justify-between gap-3 pt-2">
          <Button variant="outline" onClick={() => setStep(4)} data-testid="diagnostico-edit-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Editar mis datos
          </Button>
          <Button onClick={loadScenarios} data-testid="ver-pension-btn">
            Ver mi pensión y mis opciones <TrendingUp className="w-4 h-4 ml-1" />
          </Button>
        </div>

        <ToolFeedback tool="retirement_diagnosis" />
      </div>
    );
  };

  const renderCostoModalidad40 = (costo) => {
    if (!costo) return null;
    return (
      <p className="text-xs text-muted-foreground">
        Costo: {fmtMXN(costo.cuota_mensual)}/mes × {costo.meses} meses = <strong>{fmtMXN(costo.costo_total)} total</strong>
      </p>
    );
  };

  const renderEscenarios = () => {
    if (loadingScenarios) {
      return (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#2563EB]" />
        </div>
      );
    }
    if (!scenarios) return null;

    const ley97 = scenarios.ley_aplicable === 'Ley 97';
    const estado = scenarios.estado_elegibilidad;
    const esAsesoria = !!asesoriaCheck?.requiere_asesoria;
    const mostrarEscenario1 = estado === 'elegible';
    const mostrarM40Empleo = estado !== 'lejano';
    const esLejano = estado === 'lejano';

    const monto60 = ley97
      ? scenarios.escenario_1?.['60']?.valor_presente?.renta_mensual_estimada
      : scenarios.escenario_1?.['60']?.valor_presente?.monto_mensual_final;
    const monto65 = ley97
      ? scenarios.escenario_1?.['65']?.valor_presente?.renta_mensual_estimada
      : scenarios.escenario_1?.['65']?.valor_presente?.monto_mensual_final;

    const plazoSugeridoKey = scenarios.escenario_2?.plazo_sugerido || '1_anio';
    const plazoSugeridoLabel = PLAZO_LABELS[plazoSugeridoKey];

    return (
      <div className="space-y-8">
        {esAsesoria ? (
          <Card className="border-2 border-[#2563EB] bg-blue-50/40" data-testid="escenario-5-asesoria">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-14 h-14 rounded-xl bg-[#0F172A] flex items-center justify-center mx-auto">
                <Users className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#2563EB]">Tu próximo paso</p>
              <h3 className="text-xl font-semibold text-[#0F172A] font-['Manrope']">
                Tu situación necesita una mirada más personal — habla con nosotros o visita el IMSS
              </h3>
              <p className="text-sm text-muted-foreground max-w-lg mx-auto">
                Con lo que nos compartiste, una tabla genérica no le haría justicia a tu situación. Esto fue lo que notamos:
              </p>
              <ul className="text-sm text-left max-w-md mx-auto space-y-2">
                {asesoriaCheck.razones.map((r) => (
                  <li key={r} className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>{RAZON_ASESORIA_LABELS[r] || r}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-muted-foreground max-w-lg mx-auto">
                Esto no quiere decir que la única salida sea contratar una asesoría: para casos así, también
                puedes ir directamente a tu oficina del IMSS más cercana y revisar tu caso en persona, sin
                costo. La asesoría es una opción para que alguien te acompañe en el proceso — no la única.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button onClick={handleAsesoriaCtaClick} disabled={asesoriaCtaClicked} data-testid="asesoria-cta-btn">
                  {asesoriaCtaClicked ? (
                    <>Interés registrado <CheckCircle2 className="w-4 h-4 ml-1" /></>
                  ) : (
                    <>Solicita tu asesoría <ArrowRight className="w-4 h-4 ml-1" /></>
                  )}
                </Button>
                <a href={WHATSAPP_ASESORIA_URL} target="_blank" rel="noopener noreferrer" onClick={handleAsesoriaCtaClick}>
                  <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="asesoria-whatsapp-btn">
                    <MessageCircle className="w-4 h-4 mr-1" /> Escribir por WhatsApp
                  </Button>
                </a>
              </div>
              <p className="text-xs text-muted-foreground">Recuerda: la asesoría tiene un costo aparte y es opcional.</p>
            </CardContent>
          </Card>
        ) : estado === 'elegible' ? (
          <ProximoPasoBanner
            titulo="Ya calificas para tu pensión — ahora solo falta que decidas cuándo retirarte."
            cuerpo={
              monto60 != null && monto65 != null && monto60 !== monto65 ? (
                <>Hoy, si te retiraras, recibirías entre <strong>{fmtMXN(monto60)}</strong> y{' '}
                <strong>{fmtMXN(monto65)}</strong> al mes, dependiendo de la edad a la que te retires. Y si
                sigues cotizando (por Modalidad 40 o en un empleo formal), tu pensión puede subir todavía
                más — abajo te mostramos cómo.</>
              ) : (
                <>Hoy, si te retiraras, recibirías aproximadamente <strong>{fmtMXN(monto60 ?? monto65)}</strong>{' '}
                al mes. Si sigues cotizando, esta cifra puede subir — abajo te mostramos cómo.</>
              )
            }
            ctaLabel="Comparar mis opciones"
            ctaHref="#detalle-pension"
          />
        ) : estado === 'cercano' ? (
          <ProximoPasoBanner
            titulo={`Te faltan ${diagnosis?.semanas_faltantes ?? ''} semanas — y sí las alcanzas a cerrar.`}
            cuerpo={
              ley97 ? (
                <>Con <strong>Modalidad 40</strong> o en un <strong>empleo formal</strong>, cotizando{' '}
                <strong>{plazoSugeridoLabel}</strong> más, llegas al mínimo de {diagnosis?.semanas_minimas_requeridas} semanas que necesitas.</>
              ) : (
                <>Con <strong>Modalidad 40</strong> o en un <strong>empleo formal</strong>, cotizando{' '}
                <strong>{plazoSugeridoLabel}</strong> más, llegas a las 500 semanas que necesitas. Y en tu caso
                (Ley 73), cada semana que cotizas arriba del mínimo también te sube el porcentaje de tu
                pensión — no solo te abre la puerta a ella.</>
              )
            }
            ctaLabel={`Ver Modalidad 40 por ${plazoSugeridoLabel}`}
            ctaHref="#escenario-2"
          />
        ) : (
          <ProximoPasoBanner
            titulo="Todavía no llegas al mínimo de semanas — por ahora, tu prioridad es sumarlas, no comparar montos."
            cuerpo={
              <>
                Te faltan <strong>{scenarios.resumen_no_elegible?.semanas_faltantes} semanas</strong>
                {' '}({ley97
                  ? `este año el mínimo es ${scenarios.resumen_no_elegible?.semanas_minimas_requeridas}`
                  : 'el mínimo es 500 para calificar a pensión Ley 73'}). Ni el plazo más largo de Modalidad
                40 — 5 años — cierra esta brecha por sí solo, así que te conviene ir sumando semanas con
                calma. Puedes hacerlo en un <strong>empleo formal</strong>, sin costo directo para ti, o
                adelantarlo pagando tú mismo con <strong>Modalidad 40</strong>{' '}
                ({fmtMXN(scenarios.resumen_no_elegible?.costo_modalidad40_mensual)}/mes al tope).
                {!ley97 && (
                  <> Y en tu caso (Ley 73), estas semanas no solo te abren la puerta a la pensión: cada
                  bloque de 52 semanas arriba del mínimo te sube el porcentaje.</>
                )}{' '}
                Mientras tanto, tu saldo AFORE sigue siendo tuyo pase lo que pase.
              </>
            }
            ctaLabel="Ver qué pasaría si llegas a los 65 cotizando"
            ctaHref="#escenario-4"
          />
        )}

        {!esAsesoria && (estado === 'cercano' || estado === 'lejano') && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 -mt-4" data-testid="asesoria-opcional-link">
            <Users className="w-3.5 h-3.5 flex-shrink-0" />
            ¿Prefieres que alguien te ayude a planear esto en persona?{' '}
            <a
              href={WHATSAPP_ASESORIA_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleAsesoriaCtaClick}
              className="text-[#2563EB] hover:underline font-medium"
            >
              Solicita tu asesoría
            </a>
          </p>
        )}

        <div id="detalle-pension" className="space-y-8 pt-4 border-t scroll-mt-20">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Detalle completo</p>

          <Card className={ley97 ? 'border-amber-200 bg-amber-50' : 'border-blue-200 bg-blue-50'}>
            <CardContent className={`p-4 text-sm flex items-start gap-3 ${ley97 ? 'text-amber-900' : 'text-blue-900'}`}>
              {ley97 && <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />}
              <p>{scenarios.disclaimer_pension}</p>
            </CardContent>
          </Card>

          {ley97 && <ExplicacionAforeAccordion rendimientoAforePct={rendimientoAforePct} />}
          {!ley97 && <AforeLey73Info rendimientoAforePct={rendimientoAforePct} />}

          {/* Señales para el Escenario 5 (asesoría) — no se guardan con tu perfil */}
          <Card className="border">
            <CardContent className="p-4 space-y-3">
              <p className="text-sm font-medium text-[#0F172A]">Dos preguntas rápidas que nos ayudan a afinar tu recomendación</p>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={historialIssste} onCheckedChange={handleIsssteChange} data-testid="historial-issste-checkbox" />
                ¿Cotizaste alguna vez en el ISSSTE, además del IMSS?
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={descuentosSignificativos} onCheckedChange={handleDescuentosChange} data-testid="descuentos-significativos-checkbox" />
                ¿Tu Constancia muestra semanas descontadas que te parezcan importantes?
              </label>
            </CardContent>
          </Card>

          {!esAsesoria && (
            <>
              {mostrarEscenario1 && (
                <section className="space-y-3">
                  <h3 className="font-semibold text-[#0F172A] font-['Manrope']">Escenario 1 — Si dejas de cotizar hoy</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <PensionCell label="A los 60 años" cell={scenarios.escenario_1?.['60']} ley97={ley97} />
                    <PensionCell label="A los 65 años" cell={scenarios.escenario_1?.['65']} ley97={ley97} />
                  </div>
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <ShieldAlert className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    Si dejas de cotizar, las semanas que ya acumulaste se quedan contigo — aunque puede haber
                    reglas adicionales de vigencia de derechos que apliquen a tu caso, así que confírmalo
                    directamente con el IMSS antes de tramitar tu pensión.
                  </p>
                </section>
              )}

              {mostrarM40Empleo && (
                <>
                  {/* Escenario 2 */}
                  <section id="escenario-2" className="space-y-3 scroll-mt-20">
                    <h3 className="font-semibold text-[#0F172A] font-['Manrope']">Escenario 2 — Modalidad 40, cotizando al tope</h3>
                    <div className="flex items-center gap-3">
                      <Label className="text-sm">Plazo</Label>
                      <Select value={plazoEscenario2} onValueChange={setPlazoEscenario2}>
                        <SelectTrigger className="w-40" data-testid="plazo-escenario2-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PLAZO_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <PensionCell label={`Modalidad 40 por ${PLAZO_LABELS[plazoEscenario2]}`} cell={scenarios.escenario_2?.[plazoEscenario2]} ley97={ley97} notaAforeSoloRendimiento />
                    {renderCostoModalidad40(scenarios.escenario_2?.[plazoEscenario2]?.costo)}
                  </section>

                  {/* Escenario 3 */}
                  <section className="space-y-3">
                    <h3 className="font-semibold text-[#0F172A] font-['Manrope']">Escenario 3 — Empleo formal a salario alto</h3>
                    <p className="text-xs text-muted-foreground">
                      Es el mismo cálculo que el Escenario 2, pero cotizando dentro de un empleo formal: aquí
                      el costo lo absorbe principalmente tu patrón, vía nómina — no tú directamente, como sí
                      pasa en Modalidad 40.
                    </p>
                    <div className="flex items-center gap-3">
                      <Label className="text-sm">Plazo</Label>
                      <Select value={plazoEscenario3} onValueChange={setPlazoEscenario3}>
                        <SelectTrigger className="w-40" data-testid="plazo-escenario3-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PLAZO_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <PensionCell label={`Empleo formal por ${PLAZO_LABELS[plazoEscenario3]}`} cell={scenarios.escenario_3?.[plazoEscenario3]} ley97={ley97} notaAforeSoloRendimiento />
                  </section>
                </>
              )}

              {/* Escenario 4 */}
              <section id="escenario-4" className="space-y-3 scroll-mt-20">
                <h3 className="font-semibold text-[#0F172A] font-['Manrope']">
                  Escenario 4 — Máximo posible, cotizando al tope hasta los {scenarios.escenario_4?.edad_limite}
                </h3>
                <p className="text-xs text-muted-foreground">
                  Te quedan {scenarios.escenario_4?.anios_restantes} años según tu edad actual ({scenarios.edad_actual}).
                </p>
                <PensionCell label={`A los ${scenarios.escenario_4?.edad_limite} años`} cell={scenarios.escenario_4} ley97={ley97} notaAforeSoloRendimiento />
                {esLejano && scenarios.resumen_no_elegible && (
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <Wallet className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    Tu saldo AFORE completo hoy (Retiro + Vivienda + Voluntario) es {fmtMXN(scenarios.resumen_no_elegible.afore_lump_sum)}.
                    Es distinto del "pago único" de arriba, que solo cuenta tu subcuenta de Vivienda, porque
                    en ese escenario ya calificarías para pensión.
                  </p>
                )}
              </section>
            </>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-between gap-3 pt-2">
          <Button variant="outline" onClick={() => setStep(5)} data-testid="escenarios-back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Volver al diagnóstico
          </Button>
          <Button onClick={() => { setStep(7); loadMetaRango(); }} data-testid="ir-ahorro-meta-btn">
            Ahorro voluntario y meta inversa <Target className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  const renderContenidoEducativo = () => (
    <Card className="border bg-slate-50">
      <CardContent className="p-5 flex items-start gap-3">
        <PiggyBank className="w-5 h-5 text-[#0F172A] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-[#0F172A]">Tu AFORE siempre es tuya</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Sea Ley 73 o Ley 97, el saldo de tu AFORE es tuyo — nunca del gobierno ni del IMSS. Lo que
            cambia es cómo se usa: en Ley 73, el IMSS calcula tu pensión mensual con tu salario y tus
            semanas cotizadas, y tu AFORE se te entrega aparte, en una sola exhibición; en Ley 97, tu
            pensión sale directamente de convertir el saldo de tu AFORE en una renta. En los dos casos, si
            algo te llegara a pasar antes de pensionarte, ese saldo pasa a tus beneficiarios.
          </p>
        </div>
      </CardContent>
    </Card>
  );

  const renderAhorroVoluntario = () => (
    <section className="space-y-4">
      <h3 className="font-semibold text-[#0F172A] font-['Manrope']">Ahorro voluntario adicional</h3>
      <p className="text-sm text-muted-foreground">
        Cuéntanos cuánto podrías aportar cada mes, aparte de lo que ya ahorraste, y mira el impacto en vivo.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <NumField
          label="Aporte mensual adicional (MXN)"
          value={aporteVoluntario}
          onChange={setAporteVoluntario}
          step="0.01"
          testId="aporte-voluntario-input"
        />
        <div className="space-y-1">
          <Label>¿Por cuánto tiempo?</Label>
          <Select value={mesesVoluntario} onValueChange={setMesesVoluntario}>
            <SelectTrigger data-testid="meses-voluntario-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="12">1 año</SelectItem>
              <SelectItem value="60">5 años</SelectItem>
              <SelectItem value="120">10 años</SelectItem>
              <SelectItem value="240">20 años</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {loadingAhorro && <Loader2 className="w-5 h-5 animate-spin text-[#2563EB]" />}
      {ahorroResult && (
        <Card className="border" data-testid="ahorro-voluntario-result">
          <CardContent className="p-5 space-y-3">
            <p className="text-sm text-muted-foreground">
              Con un rendimiento nominal promedio estimado de {ahorroResult.rendimiento_nominal_anual_pct}% anual
              (el histórico del sistema de AFOREs, según la CONSAR — ver la fuente abajo), este ahorro te
              dejaría un saldo adicional de <strong>{fmtMXN(ahorroResult.saldo_proyectado_ahorro_extra)}</strong> en {ahorroResult.meses} meses.
            </p>
            {ahorroResult.nota && (
              <p className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-md p-3">{ahorroResult.nota}</p>
            )}
            {ahorroResult.elegible === false ? (
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3">{ahorroResult.motivo}</p>
            ) : ahorroResult.ley_aplicable === 'Ley 73' ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Pago único sin este ahorro</p>
                  <p className="text-lg font-bold text-[#0F172A] font-['Manrope']">{fmtMXN(ahorroResult.pago_unico_sin_ahorro_extra)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pago único con este ahorro</p>
                  <p className="text-lg font-bold text-emerald-700 font-['Manrope']">{fmtMXN(ahorroResult.pago_unico_con_ahorro_extra)}</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Renta mensual sin este ahorro</p>
                  <p className="text-lg font-bold text-[#0F172A] font-['Manrope']">{fmtMXN(ahorroResult.renta_mensual_sin_ahorro_extra)}/mes</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Renta mensual con este ahorro</p>
                  <p className="text-lg font-bold text-emerald-700 font-['Manrope']">{fmtMXN(ahorroResult.renta_mensual_con_ahorro_extra)}/mes</p>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground pt-2 border-t">{ahorroResult.fuente_rendimiento}</p>
          </CardContent>
        </Card>
      )}
    </section>
  );

  const renderRutaMeta = (result) => {
    const ruta = result.ruta;
    if (!ruta) return null;
    const esLey73 = 'semanas_adicionales_necesarias' in ruta;
    const esNivel2 = result.estado === 'nivel_2_con_asesoria';
    const tieneTiempoExtra = ruta.anios_mas_alla_del_tope_natural > 0;
    const tiempoLabel = tieneTiempoExtra ? 'Tiempo extra después de los 65' : 'Años que necesitas';
    const tiempoValor = tieneTiempoExtra ? `${ruta.anios_mas_alla_del_tope_natural} años` : ruta.anios_necesarios;

    return (
      <Card className={esNivel2 ? 'border-2 border-[#2563EB]' : 'border'} data-testid="meta-inversa-ruta">
        <CardContent className="p-5 space-y-3">
          <p className="text-sm font-semibold text-[#0F172A] font-['Manrope']">
            Tu ruta hacia {fmtMXN(result.meta_mensual)}/mes{esNivel2 ? ' (requiere asesoría)' : ''}
          </p>
          {esLey73 ? (
            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              <p>Semanas que necesitas sumar: <strong>{ruta.semanas_adicionales_necesarias}</strong></p>
              <p>{tiempoLabel}: <strong>{tiempoValor}</strong></p>
              <p>A esta edad te retirarías: <strong>{ruta.edad_retiro_resultante} años</strong></p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <p>Saldo que necesitas en tu AFORE: <strong>{fmtMXN(ruta.saldo_necesario)}</strong></p>
              <p>Saldo que te falta: <strong>{fmtMXN(ruta.saldo_faltante)}</strong></p>
              <p>{tiempoLabel}: <strong>{tiempoValor}</strong></p>
              <p>A esta edad te retirarías: <strong>{ruta.edad_retiro_resultante} años</strong></p>
            </div>
          )}
          {ruta.anios_mas_alla_del_tope_natural > 0 && (
            <p className="text-xs text-muted-foreground">
              En otras palabras: son {ruta.anios_mas_alla_del_tope_natural} años más de los que contempla el
              Escenario 4 (que llega hasta tus 65) — por eso esta meta necesita asesoría.
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t">
            <div>
              <p className="text-xs text-muted-foreground">Vía Modalidad 40</p>
              <p className="text-sm font-medium">{fmtMXN(ruta.modalidad_40.costo_mensual)}/mes · {fmtMXN(ruta.modalidad_40.costo_total)} total</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Vía empleo formal</p>
              <p className="text-sm">{ruta.empleo_formal.nota}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground pt-2 border-t">{ruta.supuesto}</p>
          {result.disclaimer_meta_inversa && (
            <p className="text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded-md p-3">{result.disclaimer_meta_inversa}</p>
          )}
          {esNivel2 && (
            <div className="flex flex-col sm:flex-row items-center gap-3 pt-3 border-t">
              <a href={WHATSAPP_ASESORIA_URL} target="_blank" rel="noopener noreferrer" onClick={handleAsesoriaCtaClick}>
                <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="meta-nivel2-whatsapp-btn">
                  <MessageCircle className="w-4 h-4 mr-1" /> Escribir por WhatsApp
                </Button>
              </a>
              <p className="text-xs text-muted-foreground">Recuerda: la asesoría tiene un costo aparte y es opcional.</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderMetaInversa = () => {
    const metaOptions = metaRango ? buildMetaOptions(metaRango.piso, metaRango.techo_natural) : [];
    return (
      <section className="space-y-4">
        <h3 className="font-semibold text-[#0F172A] font-['Manrope']">Meta inversa</h3>
        {loadingMetaRango ? (
          <Loader2 className="w-5 h-5 animate-spin text-[#2563EB]" />
        ) : metaRango && (
          <>
            <p className="text-sm text-muted-foreground">
              Este rango asume que sigues cotizando a tu salario tope — pagando Modalidad 40, o en un empleo
              formal sin costo directo para ti — hasta los 65 años. Es una ruta distinta a tu ahorro
              voluntario de arriba. Ahora dinos: ¿cuánto te gustaría recibir al mes? Sin asesoría puedes
              elegir entre <strong>{fmtMXN(metaRango.piso)}</strong> y <strong>{fmtMXN(metaRango.techo_natural)}</strong> al mes.
            </p>
            {ahorroResult?.ley_aplicable === 'Ley 97' && ahorroResult.elegible !== false && ahorroResult.renta_mensual_con_ahorro_extra != null && (
              <p className="text-xs text-muted-foreground bg-slate-50 border rounded-md p-3" data-testid="ahorro-vs-meta-referencia">
                Para comparar: con el ahorro voluntario que capturaste arriba ({fmtMXN(Number(aporteVoluntario))}/mes
                durante {ahorroResult.meses} meses) llegarías a{' '}
                <strong>{fmtMXN(ahorroResult.renta_mensual_con_ahorro_extra)}/mes</strong> — ese número viene de
                un mecanismo distinto (ahorrar en tu AFORE) al del rango de arriba (volver a cotizar formalmente).
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Label className="text-sm">Meta mensual (Nivel 1, sin asesoría)</Label>
              <Select
                value={metaSeleccionada}
                onValueChange={(v) => { setMetaSeleccionada(v); submitMetaInversa(Number(v), false); }}
              >
                <SelectTrigger className="w-48" data-testid="meta-nivel1-select">
                  <SelectValue placeholder="Selecciona un monto" />
                </SelectTrigger>
                <SelectContent>
                  {metaOptions.map((v) => (
                    <SelectItem key={v} value={String(v)}>{fmtMXN(v)}/mes</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border-t pt-4 space-y-2">
              <Label className="text-sm">¿Quieres una meta más alta? (Nivel 2, con asesoría)</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  value={metaNivel2Monto}
                  onChange={(e) => setMetaNivel2Monto(e.target.value)}
                  placeholder={`Más de ${fmtMXN(metaRango.techo_natural)}`}
                  className="w-48"
                  data-testid="meta-nivel2-input"
                />
                <Button
                  variant="outline"
                  onClick={() => submitMetaInversa(Number(metaNivel2Monto), false)}
                  disabled={!metaNivel2Monto}
                  data-testid="meta-nivel2-calcular-btn"
                >
                  Calcular
                </Button>
              </div>
            </div>

            {loadingMeta && <Loader2 className="w-5 h-5 animate-spin text-[#2563EB]" />}

            {metaResult?.estado === 'requiere_confirmacion_asesoria' && (
              <Card className="border-amber-200 bg-amber-50" data-testid="meta-nivel2-confirmacion">
                <CardContent className="p-4 space-y-3">
                  <p className="text-sm text-amber-900">{metaResult.mensaje}</p>
                  <Button
                    onClick={() => submitMetaInversa(Number(metaNivel2Monto), true)}
                    data-testid="meta-nivel2-confirmar-btn"
                  >
                    Confirmar y ver mi escenario ampliado
                  </Button>
                </CardContent>
              </Card>
            )}

            {metaResult?.ruta && renderRutaMeta(metaResult)}
          </>
        )}
      </section>
    );
  };

  const renderAhorroMeta = () => (
    <div className="space-y-8">
      {renderContenidoEducativo()}
      {renderAhorroVoluntario()}
      <div className="border-t pt-6">{renderMetaInversa()}</div>
      <div className="flex justify-start pt-2">
        <Button variant="outline" onClick={() => setStep(6)} data-testid="ahorro-meta-back-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Volver a escenarios
        </Button>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 1: return renderWelcome();
      case 2: return renderGuiaImss();
      case 3: return renderGuiaAfore();
      case 4: return renderFormulario();
      case 5: return renderDiagnostico();
      case 6: return renderEscenarios();
      case 7: return renderAhorroMeta();
      default: return null;
    }
  };

  return (
    <Layout>
      <PageSEO
        title="Planeación de Retiro: AFORE y Pensión México | Klarvi"
        description="Planea tu retiro en México: revisa tu AFORE, semanas cotizadas y proyecta tu pensión bajo Ley 73 o Ley 97. Descubre si vas por buen camino."
        canonical="https://klarvi.org/retiro"
        jsonLd={RETIREMENT_JSON_LD}
      />
      <div className="max-w-4xl mx-auto space-y-6" data-testid="retirement-page">
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A] font-['Manrope']">Planeación de Retiro</h1>
          <p className="mt-2 text-muted-foreground">
            Entiende en qué punto vas de tu retiro con el IMSS.
          </p>
        </div>

        {loadingProfile ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#2563EB]" />
          </div>
        ) : (
          <Card>
            <div className="flex border-b overflow-x-auto" data-testid="retirement-step-indicator">
              {STEPS.map((s) => {
                const Icon = s.icon;
                const active = step === s.num;
                const done = step > s.num;
                return (
                  <button
                    key={s.num}
                    onClick={() => setStep(s.num)}
                    className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-colors
                      ${active ? 'border-[#2563EB] text-[#2563EB]' : done ? 'border-transparent text-[#0F172A]' : 'border-transparent text-muted-foreground'}`}
                    data-testid={`retirement-step-tab-${s.num}`}
                  >
                    {done ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Icon className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">{s.label}</span>
                    <span className="sm:hidden">{s.num}</span>
                  </button>
                );
              })}
            </div>
            <CardContent className="pt-6">
              {renderStep()}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
